import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Howl } from 'howler';
import axios from 'axios';
import { message } from 'antd';
import FramePlayer from '../../components/FramePlayer';
import './index.less';

interface OcTrack {
    id: number;
    hashValue: string;
    bpm: number;
    link: string;
    topColor: string;
    bottomColor: string;
    createdAt: string;
    likeCount: number;
    dislikeCount: number;
    playCount: number;
    hasOcImage: boolean;
}

const OcRankingPage: React.FC = () => {
    const navigate = useNavigate();
    const [tracks, setTracks] = useState<OcTrack[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPlaying, setCurrentPlaying] = useState<string | null>(null);
    const [audio, setAudio] = useState<Howl | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [remainingTime, setRemainingTime] = useState({ days: 0, hours: 0 });
    const [collectedTracks, setCollectedTracks] = useState<Set<string>>(new Set());
    const [dislikedTracks, setDislikedTracks] = useState<Set<string>>(new Set());

    // 计算当前月份剩余时间
    const calculateRemainingTime = () => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        
        // 获取下个月的第一天
        const nextMonth = new Date(currentYear, currentMonth + 1, 1);
        
        // 计算时间差
        const timeDiff = nextMonth.getTime() - now.getTime();
        
        // 转换为天和小时
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        setRemainingTime({ days, hours });
    };

    // 定时更新剩余时间
    useEffect(() => {
        calculateRemainingTime();
        const interval = setInterval(calculateRemainingTime, 60 * 60 * 1000); // 每小时更新一次
        
        return () => clearInterval(interval);
    }, []);

    // 初始化已收藏的音轨
    useEffect(() => {
        const savedOcHashes = localStorage.getItem('scanedOcHash');
        if (savedOcHashes) {
            try {
                const hashList = JSON.parse(savedOcHashes);
                setCollectedTracks(new Set(hashList));
            } catch (error) {
                console.error('Error parsing collected tracks:', error);
            }
        }

        // 初始化已不喜欢的音轨
        const dislikedHashes = localStorage.getItem('dislikedOcHash');
        if (dislikedHashes) {
            try {
                const hashList = JSON.parse(dislikedHashes);
                setDislikedTracks(new Set(hashList));
            } catch (error) {
                console.error('Error parsing disliked tracks:', error);
            }
        }
    }, []);

    // 获取当月排名数据
    const fetchRankings = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/v1/incredibox/oc/rankings/${currentYear}/${currentMonth}`);
            if (response.data.success) {
                setTracks(response.data.data || []);
            } else {
                message.error('获取排名失败');
            }
        } catch (error) {
            console.error('Error fetching rankings:', error);
            message.error('获取排名失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRankings();
    }, [currentMonth, currentYear]);

    // 组件卸载时清理音频资源
    useEffect(() => {
        return () => {
            if (audio) {
                audio.stop();
                audio.unload();
            }
        };
    }, []);

    // 播放音频
    const handlePlay = async (track: OcTrack) => {
        try {
            // 停止当前播放的音频
            if (audio) {
                audio.stop();
                audio.unload();
            }

            if (currentPlaying === track.hashValue) {
                // 如果点击的是当前播放的音轨，则停止播放
                setCurrentPlaying(null);
                setAudio(null);
                return;
            }

            // 增加播放次数
            await axios.post(`/api/v1/incredibox/oc/play/${track.hashValue}`);

            // 创建新的音频对象
            const newAudio = new Howl({
                src: [track.link],
                html5: true,
                preload: true,
                loop: true,
                onload: () => {
                    console.log('Audio loaded');
                },
                onplay: () => {
                    setCurrentPlaying(track.hashValue);
                },
                onend: () => {
                    setCurrentPlaying(null);
                },
                onloaderror: (id: number, error: any) => {
                    console.error('Audio load error:', error);
                    message.error('音频加载失败');
                },
                onplayerror: (id: number, error: any) => {
                    console.error('Audio play error:', error);
                    message.error('音频播放失败');
                    setCurrentPlaying(null);
                }
            } as any);

            newAudio.play();
            setAudio(newAudio);

            // 更新本地播放次数
            setTracks(prev => prev.map(t => 
                t.hashValue === track.hashValue 
                    ? { ...t, playCount: t.playCount + 1 }
                    : t
            ));

        } catch (error) {
            console.error('Error playing audio:', error);
            message.error('播放失败');
        }
    };

    // 收藏（支持切换）
    const handleCollect = async (track: OcTrack) => {
        try {
            const isAlreadyCollected = collectedTracks.has(track.hashValue);

            // 获取当前localStorage中的数据
            const savedOcHashes = localStorage.getItem('scanedOcHash');
            let hashList: string[] = [];
            
            if (savedOcHashes) {
                try {
                    hashList = JSON.parse(savedOcHashes);
                } catch (error) {
                    console.error('Error parsing saved hashes:', error);
                    hashList = [];
                }
            }

            if (isAlreadyCollected) {
                // 取消收藏
                const response = await axios.post(`/api/v1/incredibox/oc/unlike/${track.hashValue}`);
                if (response.data.success) {
                    // 从localStorage中移除
                    const updatedHashList = hashList.filter(hash => hash !== track.hashValue);
                    localStorage.setItem('scanedOcHash', JSON.stringify(updatedHashList));
                    
                    // 更新本地状态
                    setCollectedTracks(prev => {
                        const newSet = new Set(Array.from(prev));
                        newSet.delete(track.hashValue);
                        return newSet;
                    });
                    
                    message.success('已取消收藏');
                    // 更新本地点赞数
                    setTracks(prev => prev.map(t => 
                        t.hashValue === track.hashValue 
                            ? { ...t, likeCount: Math.max(0, t.likeCount - 1) }
                            : t
                    ));
                } else {
                    message.error('取消收藏失败');
                }
            } else {
                // 添加收藏
                if (!hashList.includes(track.hashValue)) {
                    hashList.push(track.hashValue);
                    localStorage.setItem('scanedOcHash', JSON.stringify(hashList));
                    
                    // 更新本地状态
                    setCollectedTracks(prev => new Set([...Array.from(prev), track.hashValue]));
                    
                    // 发送点赞请求
                    const response = await axios.post(`/api/v1/incredibox/oc/like/${track.hashValue}`);
                    if (response.data.success) {
                        message.success('收藏成功');
                        // 更新本地点赞数
                        setTracks(prev => prev.map(t => 
                            t.hashValue === track.hashValue 
                                ? { ...t, likeCount: t.likeCount + 1 }
                                : t
                        ));

                        // 跳转到对应BPM的OC页面
                        window.location.href = `/?bpm=${track.bpm}&type=oc`;
                    } else {
                        message.error('收藏失败');
                    }
                } else {
                    message.info('已收藏过该音轨');
                }
            }
        } catch (error) {
            console.error('Error handling collect:', error);
            message.error('操作失败');
        }
    };

    // 踩（支持切换）
    const handleDislike = async (track: OcTrack) => {
        try {
            const isAlreadyDisliked = dislikedTracks.has(track.hashValue);
            
            // 获取当前localStorage中的数据
            const dislikedHashes = localStorage.getItem('dislikedOcHash');
            let hashList: string[] = [];
            
            if (dislikedHashes) {
                try {
                    hashList = JSON.parse(dislikedHashes);
                } catch (error) {
                    console.error('Error parsing disliked hashes:', error);
                    hashList = [];
                }
            }

            if (isAlreadyDisliked) {
                // 取消不喜欢
                const response = await axios.post(`/api/v1/incredibox/oc/undislike/${track.hashValue}`);
                if (response.data.success) {
                    // 从localStorage中移除
                    const updatedHashList = hashList.filter(hash => hash !== track.hashValue);
                    localStorage.setItem('dislikedOcHash', JSON.stringify(updatedHashList));
                    
                    // 更新本地状态
                    setDislikedTracks(prev => {
                        const newSet = new Set(Array.from(prev));
                        newSet.delete(track.hashValue);
                        return newSet;
                    });
                    
                    message.success('已取消不喜欢');
                    // 更新本地不喜欢数
                    setTracks(prev => prev.map(t => 
                        t.hashValue === track.hashValue 
                            ? { ...t, dislikeCount: Math.max(0, t.dislikeCount - 1) }
                            : t
                    ));
                } else {
                    message.error('操作失败');
                }
            } else {
                // 添加不喜欢
                const response = await axios.post(`/api/v1/incredibox/oc/dislike/${track.hashValue}`);
                if (response.data.success) {
                    // 添加到localStorage
                    if (!hashList.includes(track.hashValue)) {
                        hashList.push(track.hashValue);
                        localStorage.setItem('dislikedOcHash', JSON.stringify(hashList));
                    }
                    
                    // 更新本地状态
                    setDislikedTracks(prev => new Set([...Array.from(prev), track.hashValue]));
                    
                    message.success('已标记不喜欢');
                    // 更新本地不喜欢数
                    setTracks(prev => prev.map(t => 
                        t.hashValue === track.hashValue 
                            ? { ...t, dislikeCount: t.dislikeCount + 1 }
                            : t
                    ));
                } else {
                    message.error('操作失败');
                }
            }
        } catch (error) {
            console.error('Error handling dislike:', error);
            message.error('操作失败');
        }
    };

    // 返回上一页
    const goBack = () => {
        if (audio) {
            audio.stop();
            audio.unload();
        }
        
        // 尝试使用浏览器历史记录返回
        try {
            // 检查是否可以返回到上一页
            if (window.history.length > 1 && document.referrer) {
                // 如果来源页面是本站内的页面，使用浏览器返回
                const referrerUrl = new URL(document.referrer);
                const currentUrl = new URL(window.location.href);
                
                if (referrerUrl.origin === currentUrl.origin) {
                    window.history.back();
                    return;
                }
            }
        } catch (error) {
            console.warn('Unable to navigate back:', error);
        }
        
        // 默认返回到首页并强制刷新以确保数据加载
        window.location.href = '/';
    };

    // 切换月份
    const changeMonth = (delta: number) => {
        let newMonth = currentMonth + delta;
        let newYear = currentYear;
        
        if (newMonth > 12) {
            newMonth = 1;
            newYear += 1;
        } else if (newMonth < 1) {
            newMonth = 12;
            newYear -= 1;
        }
        
        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
    };

    return (
        <div className="oc-ranking-page">
            <div className="header">
                <button className="back-btn" onClick={goBack}> 返回 </button>
                <h1>月赛季 ------------ 剩余：{remainingTime.days}天{remainingTime.hours}小时</h1>
                {/* <div className="month-selector">
                    <button onClick={() => changeMonth(-1)}>←</button>
                    <span>{currentYear}年{currentMonth}月</span>
                    <button onClick={() => changeMonth(1)}>→</button>
                </div> */}
            </div>

            <div className="ranking-list">
                {loading ? (
                    <div className="loading">加载中...</div>
                ) : tracks.length === 0 ? (
                    <div className="empty">本月暂无OC音轨</div>
                ) : (
                    tracks.map((track, index) => (
                        <div key={track.hashValue} className="track-item">
                            <div className="rank-number">#{index + 1}</div>
                            
                            <div className="track-visual">
                                {track.hasOcImage ? (
                                    <FramePlayer
                                        basePath={track.hashValue}
                                        fileName={`${track.hashValue}-*.png`}
                                        frameCount={2}
                                        fps={track.bpm / 60 * 4}
                                        topColor={track.topColor}
                                        bottomColor={track.bottomColor}
                                        hasOcImage={true}
                                        zoomScale={0.8}
                                    />
                                ) : (
                                    <div 
                                        className="default-visual hide"
                                        // style={{
                                        //     background: `linear-gradient(${track.topColor}, ${track.bottomColor})`
                                        // }}
                                    />
                                )}
                            </div>

                            <div className="track-info">
                                {/* <div className="track-id">#{track.hashValue}</div> */}
                                <div className="track-stats">
                                    <span className="bpm">{track.bpm} BPM</span>
                                    <span className="play-count">播放: {track.playCount}</span>
                                </div>
                                <div className="created-date">
                                    {/* {new Date(track.createdAt).toLocaleDateString()} */}
                                </div>
                            </div>

                            <div className="track-actions">
                                <button 
                                    className={`play-btn ${currentPlaying === track.hashValue ? 'playing' : ''}`}
                                    onClick={() => handlePlay(track)}
                                >
                                    {currentPlaying === track.hashValue ? '⏸' : '▶'}
                                </button>
                                
                                <button 
                                    className={`collect-btn ${collectedTracks.has(track.hashValue) ? 'collected' : ''}`}
                                    onClick={() => handleCollect(track)}
                                >
                                    {collectedTracks.has(track.hashValue) ? '已收藏' : '+收藏'} 
                                    {track.likeCount}
                                </button>
                                
                                <button 
                                    className={`dislike-btn ${dislikedTracks.has(track.hashValue) ? 'disliked' : ''}`}
                                    onClick={() => handleDislike(track)}
                                >
                                    {dislikedTracks.has(track.hashValue) ? '已不喜欢' : '👎'} {track.dislikeCount}
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default OcRankingPage; 